import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import client from '../api/client';

// Driver / provider documents with the review actions (Kayd, Development Plan
// 04.11.2026 §3): view, approve, reject with a recorded reason, and replace
// a file within the admin's rights. Every decision is written to the
// activity log by the server. Statuses: Missing / Submitted for review /
// Approved / Rejected / Expired.
const STATUS = {
  missing: ['Missing', { background: '#fee2e2', color: '#991b1b' }],
  submitted: ['Submitted for review', { background: '#fef3c7', color: '#92400e' }],
  approved: ['Approved', { background: '#dcfce7', color: '#166534' }],
  rejected: ['Rejected', { background: '#fee2e2', color: '#991b1b' }],
  expired: ['Expired', { background: '#fee2e2', color: '#991b1b' }],
};

export default function ReviewDocumentList({ documents, userId, onChanged, canReview = true }) {
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const inputs = useRef({});

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onChanged && (await onChanged()); } catch (e) { toast.error(e.response?.data?.error || 'Action failed'); } finally { setBusy(null); }
  };
  const approve = (d) => act(d.document_type, () => client.patch(`/api/admin/documents/${d.id}/verify`), `${d.label} approved`);
  const reject = (d) => {
    if (!reason.trim()) { toast.error('Please enter the rejection reason.'); return; }
    act(d.document_type, () => client.patch(`/api/admin/documents/${d.id}/reject`, { reason: reason.trim() }), `${d.label} rejected`)
      .then(() => { setRejecting(null); setReason(''); });
  };
  const replace = (d, file) => {
    if (!file) return;
    act(d.document_type, async () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('bucket', 'driver-licenses');
      const { data: up } = await client.post('/api/upload', fd);
      await client.put(`/api/admin/users/${userId}/documents/${d.document_type}`, { file_path: up.path, bucket: 'driver-licenses' });
    }, `${d.label} replaced — submitted for review`);
    if (inputs.current[d.document_type]) inputs.current[d.document_type].value = '';
  };

  if (!documents || documents.length === 0) return <p style={s.empty}>No documents submitted.</p>;
  const groups = [['driver', 'Driver documents'], ['vehicle', 'Vehicle documents']];
  const hasGroups = documents.some((d) => d.group === 'vehicle');
  const sections = hasGroups ? groups.map(([g, title]) => [title, documents.filter((d) => (d.group || 'driver') === g)]) : [[null, documents]];

  return sections.map(([title, list]) => (
    <div key={title || 'all'} style={{ marginBottom: 10 }}>
      {title && <div style={s.groupTitle}>{title}</div>}
      <ul style={s.list}>
        {list.map((d) => {
          const [label, tone] = STATUS[d.status] || STATUS[d.uploaded ? 'submitted' : 'missing'];
          return (
            <li key={d.document_type} style={s.item}>
              <div style={s.row}>
                <div style={s.main}>
                  <span style={s.label}>{d.label}</span>
                  {d.required && <span style={s.required}>required</span>}
                  {d.expiry_date && <span style={s.meta}>expires {new Date(d.expiry_date).toLocaleDateString()}</span>}
                  {d.reviewed_at && <span style={s.meta}>reviewed {new Date(d.reviewed_at).toLocaleDateString()}</span>}
                </div>
                <div style={s.side}>
                  <span style={{ ...s.badge, ...tone }}>{label}</span>
                  {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" style={s.view}>View</a>}
                  {canReview && d.uploaded && d.id && d.status !== 'approved' && <button style={{ ...s.btn, ...s.green }} disabled={!!busy} onClick={() => approve(d)}>Approve</button>}
                  {canReview && d.uploaded && d.id && d.status !== 'rejected' && <button style={{ ...s.btn, ...s.red }} disabled={!!busy} onClick={() => { setRejecting(d.document_type); setReason(''); }}>Reject</button>}
                  {canReview && userId && (
                    <>
                      <input ref={(el) => { inputs.current[d.document_type] = el; }} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={(e) => replace(d, e.target.files?.[0])} />
                      <button style={s.btn} disabled={!!busy} onClick={() => inputs.current[d.document_type]?.click()}>{d.uploaded ? 'Replace' : 'Upload'}</button>
                    </>
                  )}
                </div>
              </div>
              {d.rejected_reason && d.status === 'rejected' && <div style={s.reason}>Reason: {d.rejected_reason}</div>}
              {rejecting === d.document_type && (
                <div style={s.rejectRow}>
                  <input style={s.input} placeholder="Reason (shown to the applicant)" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <button style={{ ...s.btn, ...s.red }} disabled={!!busy} onClick={() => reject(d)}>Confirm rejection</button>
                  <button style={s.btn} onClick={() => setRejecting(null)}>Cancel</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  ));
}

const s = {
  empty: { color: '#64748b', fontSize: 13, margin: 0 },
  groupTitle: { fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '6px 0' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  item: { padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  main: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 },
  label: { fontSize: 13.5, fontWeight: 600, color: '#0f172a' },
  required: { fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' },
  meta: { fontSize: 12, color: '#64748b' },
  side: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  badge: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999 },
  view: { fontSize: 12.5, fontWeight: 700, color: '#0B6A4A', textDecoration: 'none', padding: '5px 10px', border: '1px solid #a7f3d0', borderRadius: 6, background: '#ecfdf5' },
  btn: { fontSize: 12.5, fontWeight: 700, padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer' },
  green: { background: '#0f766e', color: '#fff', borderColor: '#0f766e' },
  red: { background: '#ef4444', color: '#fff', borderColor: '#ef4444' },
  reason: { fontSize: 12.5, color: '#991b1b', marginTop: 6 },
  rejectRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 },
  input: { flex: '1 1 220px', minWidth: 0, padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 },
};
